import { Link } from "react-router-dom";
import {
  HandHeart, Megaphone, Search, HeartPulse, ArrowRight, Heart,
} from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";

const ACTIONS = [
  {
    icon: HandHeart,
    title: "Aide aux malades et aux familles",
    color: "#FF6B6B",
    bg: "#FFF1F1",
    points: [
      "Écoute téléphonique et soutien moral, du diagnostic au quotidien",
      "Entraide entre malades et familles : rencontres et partages d'expérience",
      "Aide pratique : organisation des soins, déplacements, démarches",
    ],
    id: "action-aide",
  },
  {
    icon: Megaphone,
    title: "Sensibilisation",
    color: "#0284C7",
    bg: "#EFF6FF",
    points: [
      "Faire connaître la maladie de Waldmann au grand public",
      "Stands d'information, notamment lors de la Journée des maladies rares",
      "Plaquettes et contenus simples pour expliquer la maladie",
    ],
    id: "action-sensibilisation",
  },
  {
    icon: Search,
    title: "Recherche d'informations",
    color: "#10B981",
    bg: "#ECFDF5",
    points: [
      "Veille sur les avancées médicales et les publications sur la maladie",
      "Traduction en langage clair des informations médicales",
      "Orientation vers des sources fiables et reconnues",
    ],
    id: "action-recherche",
  },
  {
    icon: HeartPulse,
    title: "Amélioration de la prise en charge",
    color: "#0F2C59",
    bg: "#F1F5F9",
    points: [
      "Financement d'aides concrètes : soutien alimentaire et soutien financier au quotidien",
      "Soutien aux initiatives qui simplifient le parcours de soins",
      "Dialogue avec les équipes hospitalières expérimentées",
    ],
    id: "action-prise-en-charge",
  },
];

const Actions = () => (
  <div data-testid="actions-page">
    <PageHero
      eyebrow="Nos actions"
      title="Agir concrètement, chaque semaine"
      description="Notre association agit sur quatre terrains : soutenir les malades, faire connaître la maladie, chercher et partager l'information, et améliorer la prise en charge."
      testid="actions-title"
    />

    <section className="py-16 md:py-24">
      <div className="container-site grid gap-8 md:grid-cols-2">
        {ACTIONS.map((a, i) => (
          <Reveal key={a.id} delay={(i % 2) * 0.12}>
            <article data-testid={a.id} className="card-site h-full !p-8 sm:!p-10">
              <span
                className="inline-flex h-14 w-14 items-center justify-center rounded-2xl"
                style={{ backgroundColor: a.bg, color: a.color }}
              >
                <a.icon size={26} />
              </span>
              <h2 className="mt-6 font-heading text-xl sm:text-2xl font-semibold tracking-tight text-[#0F2C59]">
                {a.title}
              </h2>
              <ul className="mt-5 space-y-3">
                {a.points.map((p, j) => (
                  <li key={j} className="flex items-start gap-3 text-sm sm:text-base font-medium leading-relaxed text-slate-600">
                    <span className="mt-2 h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: a.color }} />
                    {p}
                  </li>
                ))}
              </ul>
            </article>
          </Reveal>
        ))}
      </div>
    </section>

    <section className="pb-20 md:pb-28">
      <div className="container-site">
        <Reveal>
          <div className="rounded-[2rem] bg-[#0F2C59] p-8 sm:p-14 text-center">
            <Heart size={40} className="mx-auto text-[#FF6B6B]" fill="currentColor" />
            <h2 className="mt-6 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white leading-tight">
              Ces actions n'existent que grâce à vous
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-sky-100/85">
              Dons, bénévolat, relais sur les réseaux : chaque geste nous aide à soutenir les
              malades de Waldmann et leurs familles. Rejoignez le mouvement.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-4">
              <Link to="/faire-un-don" data-testid="actions-donate-button" className="btn-don don-pulse !px-8 !py-4">
                <Heart size={18} strokeWidth={2.5} fill="currentColor" /> Faire un don
              </Link>
              <Link to="/contact" data-testid="actions-volunteer-button" className="inline-flex items-center gap-2 rounded-full border-2 border-white/40 px-7 py-3.5 text-base font-bold text-white transition-colors hover:bg-white/10">
                Devenir bénévole <ArrowRight size={17} />
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  </div>
);

export default Actions;
